import { NavLink } from "react-router";
import { Icon } from "../ui/Icon";
import AltArrowRightLinear from "~icons/solar/alt-arrow-right-linear";
import AiLine from "~icons/mingcute/ai-line";
import dahlingLogoWhite from "../../assets/dahling_logo_white.svg";
import { cn } from "../../lib/utils";
import { useSidebarNav } from "./useSidebarNav";
import type { NavItemDef } from "./useSidebarNav";
import { usePermissions } from "../../hooks/usePermissions";
import { Permissions } from "../../types/permissions";

function CollapseIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <rect x="2" y="2" width="20" height="20" rx="2.5" fill="currentColor" />
      <rect x="4" y="4" width="8" height="16" rx="2" fill="#2A2A2A" />
    </svg>
  );
}

interface SidebarItemRenderProps extends NavItemDef {
  collapsed: boolean;
}

function SidebarItem({
  to,
  icon,
  label,
  end,
  collapsed,
  onClick,
}: SidebarItemRenderProps) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center rounded-full p-3 text-body-md transition-colors",
          isActive
            ? "sidebar-item-active font-bold text-white"
            : "font-normal text-text-placeholder hover:text-white",
          collapsed ? "justify-center" : "gap-[15px]"
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon icon={icon} size="lg" />
          {!collapsed && <span className="flex-1">{label}</span>}
          {!collapsed && isActive && (
            <Icon icon={AltArrowRightLinear} size="md" />
          )}
        </>
      )}
    </NavLink>
  );
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { navItems, handleNewChat } = useSidebarNav();
  const { hasPermission } = usePermissions();
  const canChat = hasPermission(Permissions.AI_CHAT);

  return (
    <aside
      className={cn(
        "hidden shrink-0 flex-col gap-5 bg-sidebar-bg pb-4 transition-all duration-300 lg:flex",
        collapsed ? "w-20 items-center px-2" : "w-[272px] px-5"
      )}
    >
      <div
        className={cn(
          "flex h-[83px] items-center",
          collapsed ? "justify-center" : "justify-between"
        )}
      >
        {!collapsed && (
          <img src={dahlingLogoWhite} alt="Dahling" width={135} height={32} />
        )}
        <button
          type="button"
          onClick={onToggle}
          className="text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <CollapseIcon />
        </button>
      </div>

      <nav>
        <ul className="flex flex-col gap-1">
          {navItems.map((item) => (
            <li key={item.label}>
              <SidebarItem {...item} collapsed={collapsed} />
            </li>
          ))}
        </ul>
      </nav>

      {!collapsed && canChat && (
        <div className="mt-auto">
          <div className="flex flex-col items-center gap-3 rounded-xl bg-secondary p-3 shadow-[0px_10px_30px_0px_rgba(0,0,0,0.35)]">
            <h3 className="text-body-lg font-bold text-white">
              Meet dahling AI
            </h3>
            <p className="text-center text-body-sm text-white/70">
              Unlock powerful AI to automate your workflow—from prepping
              interview questions to summarizing key insights.
            </p>
            <button
              type="button"
              onClick={handleNewChat}
              className="flex w-full items-center justify-center gap-1 rounded-full bg-gradient-to-r from-gradient-start to-gradient-end px-4 py-2.5 text-body-md font-bold text-white"
            >
              <Icon icon={AiLine} size="md" />
              Chat with AI now
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
